import { useState, useEffect, useMemo } from 'react';
import {
  DEFAULT_LOCAL_SPEND_TOGGLE_YEAR,
  OPPS_DATA,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME_LOCAL,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME_LOCAL,
  WEEKLY,
  fiscalMonths,
} from '../constants';
import { getOTPayload, getSpendsAndOppsData } from '../service/SpendAndOppurtunitiesService';

export const useOverTimeDataHooks = (
  selectedStore,
  currentYear,
  viewType,
  userStores,
  dataType,
  spendToggleState
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const branchOptions = useMemo(() => {
    return userStores
      .map((store) => ({
        label: store.branch_name,
        value: store.branch_number,
        ...store,
      }))
      .sort((a, b) => a.value - b.value);
  }, [userStores]);

  let compareWith = useMemo(() => {
    if (branchOptions.find((store) => store.value === selectedStore)?.label === 'GALE Markets') {
      return [
        { label: 'None', value: 'None' },
        { label: 'Store Region', value: 'Store Region' },
      ];
    }
    return [
      { label: 'None', value: 'None' },
      { label: 'GALE Markets', value: 'GALE Markets' },
      { label: 'Store Region', value: 'Store Region' },
    ];
  }, [branchOptions, selectedStore]);

  const [xAxisOOTData, setXAxisOOTData] = useState([]);

  const [currActualValues, setCurrActualValues] = useState([]);
  const [currPlannedValues, setCurrPlannedValues] = useState([]);

  const [currActualValuesWeekly, setCurrActualValuesWeekly] = useState([]);
  const [currPlannedValuesWeekly, setCurrPlannedValuesWeekly] = useState([]);

  const [overTimeSeries, setOverTimeSeries] = useState([]);
  const [overTimeLegend, setOverTimeLegend] = useState([
    `${currentYear} Planned - ${branchOptions?.find((store) => store?.value === selectedStore)?.label}`,
    `${currentYear} Actual - ${branchOptions?.find((store) => store?.value === selectedStore)?.label}`,
  ]);
  const [selectedYears, setSelectedYears] = useState([currentYear]);
  const [selectedComparisonStore, setSelectedComparisonStore] = useState('');
  const [displayWeekly, setDisplayWeekly] = useState(false);

  function transformOverTimeData(data, type) {
    let actualValues = [],
      plannedValues = [],
      actualValesWeekly = [],
      plannedValuesWeekly = [],
      storeAggregatedGale = [],
      storeAggregatedRegion = [],
      pastYears = {},
      hasRegionID = false;

    const filteredData = data.filter((eachData) => eachData.fiscal_month !== null);
    let sortArr = [];
    if (type === WEEKLY) {
      sortArr = filteredData.slice().sort((a, b) => a.fiscal_week - b.fiscal_week);
    } else {
      sortArr = filteredData.slice().sort((a, b) => a.fiscal_month - b.fiscal_month);
    }

    if (branchOptions.filter((store) => store.value === selectedStore)[0].region) {
      hasRegionID = true;
    }

    const getStoreLabel = () =>
      branchOptions?.find((store) => store?.value === selectedStore)?.label;

    sortArr.forEach((entry) => {
      const isCurrentYear = entry.fiscal_year === currentYear;
      const storeLabel = getStoreLabel();

      // Create keys for past years data once to avoid duplication
      const actualYearKey = `${entry.fiscal_year} Actual - ${storeLabel}`;
      const plannedYearKey = `${entry.fiscal_year} Planned - ${storeLabel}`;

      if (dataType === OPPS_DATA) {
        let actual_opps = type === WEEKLY ? entry.opps_avg : entry.actual_opps;
        let planned_opps = type === WEEKLY ? entry.opps_plan_avg : entry.opps_plan;
        let actual_opps_weekly =
          type === WEEKLY && !displayWeekly ? entry.actual_opps : actual_opps;
        let planned_opps_weekly =
          type === WEEKLY && !displayWeekly ? entry.opps_plan : planned_opps;

        if (isCurrentYear) {
          actualValues.push(actual_opps);
          type === WEEKLY && actualValesWeekly.push(actual_opps_weekly);
          plannedValues.push(planned_opps?.toFixed(2));
          type === WEEKLY && !displayWeekly && plannedValuesWeekly.push(planned_opps_weekly);

          if (hasRegionID) {
            entry.gale_avg_opps && storeAggregatedGale.push(entry.gale_avg_opps?.toFixed(2));
            entry.region_avg_opps && storeAggregatedRegion.push(entry.region_avg_opps?.toFixed(2));
          }
        } else {
          // Handle past years data
          updatePastYears(
            pastYears,
            actualYearKey,
            plannedYearKey,
            actual_opps_weekly?.toFixed(2),
            planned_opps_weekly?.toFixed(2)
          );
        }
      } else {
        let actual_spend = type === WEEKLY ? entry.spend_avg : entry.actual_spend;
        let planned_spend = type === WEEKLY ? entry.spend_plan_avg : entry.spend_plan;
        let actual_spend_weekly =
          type === WEEKLY && !displayWeekly ? entry.actual_spend : actual_spend;
        let planned_spend_weekly =
          type === WEEKLY && !displayWeekly ? entry.spend_plan : planned_spend;

        if (isCurrentYear) {
          actualValues.push(actual_spend);
          type === WEEKLY && actualValesWeekly.push(actual_spend_weekly);
          plannedValues.push(planned_spend?.toFixed(2));
          type === WEEKLY && plannedValuesWeekly.push(planned_spend_weekly);

          if (hasRegionID) {
            entry.gale_avg_spend && storeAggregatedGale.push(entry.gale_avg_spend?.toFixed(2));
            entry.region_avg_spend &&
              storeAggregatedRegion.push(entry.region_avg_spend?.toFixed(2));
          }
        } else {
          // Handle past years data
          updatePastYears(
            pastYears,
            actualYearKey,
            plannedYearKey,
            actual_spend_weekly?.toFixed(2),
            planned_spend_weekly?.toFixed(2)
          );
        }
      }
    });

    // Helper function to update past years data
    function updatePastYears(pastYears, actualKey, plannedKey, actualValue, plannedValue) {
      if (pastYears[actualKey]?.length || pastYears[plannedKey]?.length) {
        pastYears[actualKey] = [...pastYears[actualKey], actualValue];
        pastYears[plannedKey] = [...pastYears[plannedKey], plannedValue];
      } else {
        pastYears[actualKey] = [actualValue];
        pastYears[plannedKey] = [plannedValue];
      }
    }

    // Set X-axis values
    let xAxisValues = [
      ...new Set(
        sortArr?.map((item) =>
          type === WEEKLY ? `W${item?.fiscal_week}` : fiscalMonths[item?.fiscal_month]
        )
      ),
    ];

    setCurrActualValues(actualValues);
    setCurrActualValuesWeekly(actualValesWeekly);
    setCurrPlannedValues(plannedValues);
    setCurrPlannedValuesWeekly(plannedValuesWeekly);
    setXAxisOOTData(xAxisValues);

    const legendValue = selectedYears.map(String);
    const storeLabel = getStoreLabel();

    if (selectedYears.includes(currentYear)) {
      setOverTimeLegend([
        `${currentYear} Planned - ${storeLabel}`,
        `${currentYear} Actual - ${storeLabel}`,
        ...generateLegends(legendValue.filter((x) => !x.includes(currentYear))),
      ]);
    } else {
      setOverTimeLegend(generateLegends(legendValue));
    }

    // Create series data with a common style configuration
    const lineConfig = {
      type: 'line',
      lineStyle: { width: 2 },
      itemStyle: { borderWidth: 2, borderColor: '#a5b0af' },
    };

    // Build series array
    let seriesValueArr = [
      {
        name: `${currentYear} Actual - ${storeLabel}`,
        data: !displayWeekly && type === WEEKLY ? actualValesWeekly : actualValues,
        ...lineConfig,
      },
      {
        name: `${currentYear} Planned - ${storeLabel}`,
        data: !displayWeekly && type === WEEKLY ? plannedValuesWeekly : plannedValues,
        ...lineConfig,
      },
    ];

    // Add comparison data
    if (selectedComparisonStore === 'GALE Markets' && storeAggregatedGale?.length) {
      seriesValueArr.push({
        name: 'GALE Markets Average',
        data: storeAggregatedGale,
        ...lineConfig,
      });
    }

    if (selectedComparisonStore === 'Store Region' && storeAggregatedRegion?.length) {
      seriesValueArr.push({
        name: 'Regional Store Average',
        data: storeAggregatedRegion,
        ...lineConfig,
      });
    }

    // Add past years data
    if (Object.keys(pastYears)?.length) {
      Object.keys(pastYears).forEach((year) => {
        seriesValueArr.push({
          name: year,
          data: pastYears[year],
          ...lineConfig,
        });
      });
    }

    // Sort seriesValueArr by name in descending order with null/undefined check
    seriesValueArr?.sort((a, b) => {
      const nameA = a?.name || '';
      const nameB = b?.name || '';
      return nameB?.localeCompare(nameA);
    });

    setOverTimeSeries(seriesValueArr);
  }

  const generateLegends = (years) => {
    let pastYearLegends = [];
    years.forEach((year) => {
      pastYearLegends = [
        ...pastYearLegends,
        `${year} Planned - ${branchOptions?.find((store) => store?.value === selectedStore)?.label}`,
        `${year} Actual - ${branchOptions?.find((store) => store?.value === selectedStore)?.label}`,
      ];
    });
    return pastYearLegends;
  };
  const handleOOTStoreChange = (newSelectedStore) => {
    setSelectedComparisonStore(newSelectedStore);
  };
  const handleYearChange = (newSelectedYears) => {
    if (!newSelectedYears?.length) {
      setOverTimeSeries([]);
    }
    setSelectedYears(newSelectedYears);
  };

  const fetchAndTransformData = async () => {
    setIsLoading(true);
    setError(null);
    setCurrActualValuesWeekly(null);
    setCurrPlannedValuesWeekly(null);
    try {
      const monthlyKey = !spendToggleState
        ? SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME
        : SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME_LOCAL;
      const weeklyKey = !spendToggleState
        ? SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME
        : SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME_LOCAL;
      const key = viewType === 'Monthly' ? monthlyKey : weeklyKey;
      const payload = getOTPayload(key, selectedStore, selectedYears);
      const response = await getSpendsAndOppsData(payload, key, dataType);
      if (response.success) {
        transformOverTimeData(response.data[key].result, viewType);
      } else {
        throw new Error('Data fetch was successful but data structure was unexpected.');
      }
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedStore !== undefined && selectedYears.length > 0 && viewType !== undefined) {
        if (spendToggleState) {
          const filteredYears = selectedYears?.filter(
            (year) => year >= DEFAULT_LOCAL_SPEND_TOGGLE_YEAR
          );
          if (filteredYears?.length !== selectedYears?.length) {
            handleYearChange(filteredYears);
            return; // Exit early to avoid calling fetchAndTransformData with incorrect years
          }
        }
        fetchAndTransformData();
      }
    }, 1000); // 1000ms delay

    // Cleanup function to clear the timeout if the value changes before the delay is over
    return () => clearTimeout(timer);
  }, [
    selectedStore,
    viewType,
    selectedComparisonStore,
    selectedYears,
    spendToggleState,
    displayWeekly,
  ]);

  return {
    selectedYears,
    isLoading,
    error,
    overTimeSeries,
    currActualValues,
    currPlannedValues,
    overTimeLegend,
    xAxisOOTData,
    handleYearChange,
    handleOOTStoreChange,
    selectedComparisonStore,
    compareWith,
    displayWeekly,
    setDisplayWeekly,
    currActualValuesWeekly,
    currPlannedValuesWeekly,
  };
};
